import { jsx, jsxs } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { useMergedCSS, styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useId } from '@radix-ui/react-id';
import classNames from 'classnames';
import { forwardRef, useState, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import useInputValue from '../../hooks/useInputValue.mjs';
import { sharedStyledRoot, sharedStyledInputRoot, sharedStyledContent, sharedStylePlaceholder, sharedStyledInputPlaceholderRoot, sharedStyledLabelRoot } from '../sharedStyled.vanilla.mjs';
import { useTextAreaContext } from './context.mjs';
import { styledAutoSizeTextArea, baseTextAreaRoot, styledTextArea, styledTextAreaPlaceholder, styledInputPlaceholderRoot, styledRoot, styledLabelRoot, BOTTOM_PADDING } from './textArea.vanilla.mjs';

const BaseTextArea = forwardRef(
  ({
    label,
    labelledBy,
    describedBy,
    helperText,
    size = "default",
    variant,
    state,
    disabled,
    css: cssFromProps,
    style,
    className,
    children,
    spellCheck = false,
    ...props
  }, forwardedRef) => {
    const inputId = useId(props?.id);
    const { footerElement } = useTextAreaContext("TextArea.BaseTextArea");
    const paddingBottom = Math.max(
      footerElement?.offsetHeight ?? 0,
      BOTTOM_PADDING[size]
    );
    const css = useMergedCSS(cssFromProps, {
      paddingBottom
    });
    const isInvalid = state === "error" ? true : false;
    if (isInputSizeLabelInsideType(size)) {
      return /* @__PURE__ */ jsx(
        LabelInsideTextArea,
        {
          ...props,
          size,
          label,
          helperText,
          variant,
          state,
          disabled,
          css,
          style,
          className,
          "aria-describedby": describedBy,
          "aria-labelledby": labelledBy,
          "aria-invalid": isInvalid,
          id: inputId,
          ref: forwardedRef,
          spellCheck,
          children
        }
      );
    }
    return /* @__PURE__ */ jsxs("div", { className: sharedStyledRoot, "data-scope": "text-area", "data-part": "root", children: [
      label,
      /* @__PURE__ */ jsxs(
        StyledBaseTextAreaRoot,
        {
          className: classNames(
            sharedStyledInputRoot({ disabled, state, variant }),
            baseTextAreaRoot({ size }),
            className
          ),
          css,
          children: [
            /* @__PURE__ */ jsx(
              TextareaAutosize,
              {
                className: styledAutoSizeTextArea({ size }),
                ref: forwardedRef,
                "aria-describedby": describedBy,
                "aria-labelledby": labelledBy,
                "aria-invalid": isInvalid,
                id: inputId,
                disabled,
                spellCheck,
                ...props
              }
            ),
            children
          ]
        }
      ),
      helperText
    ] });
  }
);
function isInputSizeLabelInsideType(size) {
  return size === "xlarge";
}
const LabelInsideTextArea = forwardRef((props, forwardedRef) => {
  const {
    label,
    labelledBy,
    value: valueFromProps,
    defaultValue,
    variant,
    state,
    size,
    disabled,
    placeholder,
    helperText,
    onChange: onChangeFromProps,
    css,
    style,
    className,
    children,
    ...restProps
  } = props;
  const [inputActive, setInputActive] = useState(false);
  const [value, onChange] = useInputValue({
    value: valueFromProps,
    defaultValue,
    onChange: onChangeFromProps
  });
  const isValueEmpty = isNil(value) || value === "";
  const hasLabelElement = !isNil(label);
  useEffect(() => {
    if (!isNil(value) && value !== "") {
      setInputActive(true);
    }
  }, [value]);
  return /* @__PURE__ */ jsxs("div", { className: sharedStyledRoot, children: [
    /* @__PURE__ */ jsx("div", { className: sharedStyledContent, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: classNames(
          sharedStyledInputRoot({ disabled, state, variant }),
          baseTextAreaRoot({ size })
        ),
        children: [
          /* @__PURE__ */ jsx(
            StyledBaseTextAreaRoot,
            {
              css,
              className: classNames(sharedStyledRoot, styledRoot, className),
              style,
              children: /* @__PURE__ */ jsxs(
                "div",
                {
                  className: classNames(
                    sharedStyledInputPlaceholderRoot,
                    styledInputPlaceholderRoot
                  ),
                  children: [
                    /* @__PURE__ */ jsx(
                      TextareaAutosize,
                      {
                        ...restProps,
                        disabled,
                        className: classNames(
                          styledAutoSizeTextArea({ size }),
                          styledTextArea({ hasLabel: hasLabelElement })
                        ),
                        ref: forwardedRef,
                        onChange,
                        value,
                        onFocus: composeEventHandlers(
                          props.onFocus,
                          () => setInputActive(true)
                        ),
                        onBlur: composeEventHandlers(
                          props.onBlur,
                          () => isValueEmpty && setInputActive(false)
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "p",
                      {
                        className: classNames(
                          sharedStylePlaceholder({
                            inactive: inputActive,
                            required: props.required
                          }),
                          styledTextAreaPlaceholder({ active: inputActive, size })
                        ),
                        children: placeholder
                      }
                    )
                  ]
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: classNames(
                sharedStyledLabelRoot,
                styledLabelRoot({ inactive: !inputActive })
              ),
              children: label
            }
          ),
          children
        ]
      }
    ) }),
    helperText
  ] });
});
const StyledBaseTextAreaRoot = styled("div");

export { BaseTextArea as default };
//# sourceMappingURL=BaseTextArea.mjs.map
